import { FC, useContext, useState } from "react";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import { useI18n } from "../../hooks/useTranslations";
import { AdminCarDriver } from "../../http/carDrivers/types";
import { CarPlate } from "../../http/carPlates/types";
import { useCarSubsidiariesApi } from "../../http/carSubsidiaries/api";
import { CarSubsidiary, OpenEntities, UpdateCarSubsidiary } from "../../http/carSubsidiaries/types";
import { CarTask } from "../../http/carTasks/types";
import { DueDate } from "../../http/dueDates/types";
import { HighwayPass } from "../../http/highwayPasses/types";
import { OtherCarAssignment } from "../../http/otherCarAssignment/types";
import { formatDateNumeric } from "../../types/utils";
import EditCarDriverDialog from "../carDrivers/EditCarDriverDialog";
import EditCarPlateDialog from "../carPlates/EditCarPlateDialog";
import DeleteCarTaskDialog from "../carTask/DeleteCarTaskDialog";
import EditDueDate from "../dueDates/EditDueDateDialog";
import EditHighwayPassDialog from "../highwayPasses/EditHighwayPassButton";
import EditOtherCarAssignmentDialog from "../otherCarAssignment/EditOtherCarAssignmentDialog";
import { WarningBox } from "../ui/Boxes";
import Button from "../ui/Button";
import ModalDialog from "../ui/ModalDialog";
import SubmitBar from "../ui/SubmitBar";
import Table from "../ui/Table";
import { CarSubsidiaryFormBody, CarSubsidiarySearch } from "./CarSubsidiaryForm";
import styles from "./OpenEntities.module.css";

type OpenEntitiesProps = {
  carSubsidiary?: CarSubsidiary | null;
  currentCarSubsidiary?: CarSubsidiarySearch;
  openEntities: OpenEntities;
  onClose: () => void;
  formData: CarSubsidiaryFormBody;
  onUpdate: () => void;
  onCloseFormDialog: () => void;
};

const OpenEntitiesDialog: FC<OpenEntitiesProps> = ({
  openEntities,
  carSubsidiary,
  currentCarSubsidiary,
  onClose,
  formData,
  onUpdate,
  onCloseFormDialog,
}: OpenEntitiesProps) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const carSubsidiariesApi = useCarSubsidiariesApi();
  // const carTaskLogsApi = useCarTaskLogsApi();

  const [plateToEdit, setPlateToEdit] = useState<CarPlate | undefined>(undefined);
  const [carDriverToEdit, setCarDriverToEdit] = useState<AdminCarDriver | undefined>(undefined);
  const [assignmentToEdit, setAssignmentToEdit] = useState<OtherCarAssignment | undefined>(undefined);
  const [dueDateToEdit, setDueDateToEdit] = useState<DueDate | undefined>(undefined);
  const [highwayPassToEdit, setHighwayPassToEdit] = useState<HighwayPass | undefined>(undefined);
  const [carTaskToDelete, setCarTaskToDelete] = useState<CarTask>();
  const [reOpenEntities, setReOpenEntities] = useState<OpenEntities | undefined>(undefined);

  const onSubmitAssign = async () => {
    if (currentCarSubsidiary && formData.prevEndDate) {
      try {
        await carSubsidiariesApi.upsertCarSubsidiary(
          {
            carSubsidiary: {
              subsidiaryId: formData.subsidiary.id,
              startDate: formData.startDate,
              endDate: formData.endDate,
              prevEndDate: formData.prevEndDate,
              currentVersionNumber: currentCarSubsidiary.versionNumber,
            },
            entitiesTOReopen: reOpenEntities,
          },
          currentCarSubsidiary.car.id,
        );
        onUpdate();
        onClose();
        onCloseFormDialog();
        dispatch(showSuccessNotification(i18n.t));
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      }
    }
  };

  const onSubmit = async () => {
    if (carSubsidiary) {
      const body: UpdateCarSubsidiary = {
        subsidiaryId: formData.subsidiary.id,
        startDate: formData.startDate,
        endDate: formData.endDate,
        currentVersionNumber: carSubsidiary.versionNumber,
        closeOpenEntities: true,
      };

      try {
        await carSubsidiariesApi.updateCarSubsidiary(carSubsidiary.id, body, carSubsidiary.car.id);
        onUpdate();
        onClose();
        onCloseFormDialog();
        dispatch(showSuccessNotification(i18n.t));
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      }
    }
  };

  // const toggle = async (carTask: CarTask, state: CarTaskState) => {
  //   try {
  //     await carTaskLogsApi.insertAdminCarTaskLog(
  //       carTask.id,
  //       {
  //         state: state,
  //       },
  //       carTask.car.id
  //     );
  //     dispatch(showSuccessNotification(i18n.t));
  //     onUpdate();
  //   } catch (err) {
  //     dispatch(showErrorNotification(i18n, err));
  //   }
  // };

  // const rowActionsTasks: Action<CarTask>[] = [
  //   {
  //     title: i18n.t.common.setAsDone,
  //     onClick: (item) => toggle(item, CarTaskDone),
  //     icon: SvgCheckCircle,
  //     color: "green",
  //   },
  //   {
  //     title: i18n.t.common.setAsNotRequired,
  //     onClick: (item) => toggle(item, CarTaskNotRequired),
  //     icon: SvgDoNotDisturbOn,
  //     color: "blue",
  //   },

  //   getDeleteAction(i18n, (item) => setCarTaskToDelete(item), true),
  // ];

  const onToggleRowCarPlate = (item: CarPlate, checked: boolean) => {
    if (checked) {
      setReOpenEntities({ ...reOpenEntities!, carPlate: item });
    } else {
      setReOpenEntities({ ...reOpenEntities!, carPlate: undefined });
    }
  };

  const onToggleRowAssignment = (item: OtherCarAssignment, checked: boolean) => {
    if (checked) {
      setReOpenEntities({ ...reOpenEntities!, otherAssignment: item });
    } else {
      setReOpenEntities({ ...reOpenEntities!, otherAssignment: undefined });
    }
  };

  const onToggleRowCarDriver = (item: AdminCarDriver, checked: boolean) => {
    if (checked) {
      setReOpenEntities({ ...reOpenEntities!, carDriver: item });
    } else {
      setReOpenEntities({ ...reOpenEntities!, carDriver: undefined });
    }
  };

  const onToggleRowHighwayPass = (item: HighwayPass, checked: boolean) => {
    if (checked) {
      setReOpenEntities({ ...reOpenEntities!, highwayPass: item });
    } else {
      setReOpenEntities({ ...reOpenEntities!, highwayPass: undefined });
    }
  };

  // const onToggleRowDueDate = (item: DueDate, checked: boolean) => {
  //   setReOpenEntities((prev) => {
  //     if (checked) {
  //       return {
  //         ...prev,
  //         dueDates: [...(prev.dueDates || []), item],
  //       };
  //     } else {
  //       return {
  //         ...prev,
  //         dueDates: (prev.dueDates || []).filter((d) => d.id !== item.id),
  //       };
  //     }
  //   });
  // };

  // const onToggleRowCarTask = (item: CarTask, checked: boolean) => {
  //   setReOpenEntities((prev) => {
  //     if (checked) {
  //       return {
  //         ...prev,
  //         carTasks: [...(prev.carTasks || []), item],
  //       };
  //     } else {
  //       return {
  //         ...prev,
  //         carTasks: (prev.carTasks || []).filter((t) => t.id !== item.id),
  //       };
  //     }
  //   });
  // };

  return (
    <>
      <ModalDialog
        onClose={onClose}
        title={i18n.t.common.dataConflicts(
          formData.endDate
            ? formatDateNumeric(formData.endDate, i18n.language)
            : formatDateNumeric(formData.prevEndDate!, i18n.language),
        )}
      >
        {openEntities.carPlate && (
          <div>
            <h4>{i18n.t.carPlates.plural}</h4>
            <Table<CarPlate>
              head={
                <>
                  {!!currentCarSubsidiary && <th></th>}
                  <th align="left">{i18n.t.common.number}</th>
                  <th align="left">{i18n.t.common.licensePlateValidityStart}</th>
                  <th align="left">{i18n.t.common.licensePlateValidityEnd}</th>
                </>
              }
              renderRow={(item) => (
                <>
                  <td>{item.nr}</td>
                  <td>{formatDateNumeric(item.startDate, i18n.language)}</td>
                  <td>{!!item.endDate && formatDateNumeric(item.endDate, i18n.language)}</td>
                </>
              )}
              selectable={
                !!currentCarSubsidiary
                  ? {
                      onRowSelect: onToggleRowCarPlate,
                      isRowSelected: (item) => reOpenEntities?.carPlate?.id == item.id,
                      isRowSelectable: (item) => !!item,
                    }
                  : undefined
              }
              onRowClick={(item) => setPlateToEdit(item)}
              items={openEntities.carPlate ? [openEntities.carPlate] : []}
            ></Table>
          </div>
        )}
        {openEntities.carDriver && (
          <div>
            <h4>{i18n.t.carDrivers.plural}</h4>
            <Table<AdminCarDriver>
              head={
                <>
                  {!!currentCarSubsidiary && <th></th>}
                  <th align="left">{i18n.t.drivers.singular}</th>
                  <th align="left">{i18n.t.common.startDate}</th>
                  <th align="left">{i18n.t.common.endDate}</th>
                </>
              }
              renderRow={(item) => (
                <>
                  <td>{item.driver.fullName}</td>
                  <td>{formatDateNumeric(item.startDate, i18n.language)}</td>
                  <td>{!!item.endDate && formatDateNumeric(item.endDate, i18n.language)}</td>
                </>
              )}
              selectable={
                !!currentCarSubsidiary
                  ? {
                      onRowSelect: onToggleRowCarDriver,
                      isRowSelected: (item) => reOpenEntities?.carDriver?.id == item.id,
                      isRowSelectable: (item) => !!item,
                    }
                  : undefined
              }
              items={openEntities.carDriver ? [openEntities.carDriver] : []}
              onRowClick={(item) => setCarDriverToEdit(item)}
            ></Table>
          </div>
        )}
        {openEntities.otherAssignment && (
          <div>
            <h4>{i18n.t.otherCarAssignments.plural}</h4>
            <Table<OtherCarAssignment>
              head={
                <>
                  {!!currentCarSubsidiary && <th></th>}
                  <th align="left">{i18n.t.drivers.singular} </th>
                  <th align="left">{i18n.t.common.startDate} </th>
                  <th align="left">{i18n.t.common.endDate} </th>
                </>
              }
              renderRow={(item) => (
                <>
                  <td>{item.type}</td>
                  <td>{formatDateNumeric(item.startDate, i18n.language)}</td>
                  <td>{!!item.endDate && formatDateNumeric(item.endDate, i18n.language)}</td>
                </>
              )}
              selectable={
                !!currentCarSubsidiary
                  ? {
                      onRowSelect: onToggleRowAssignment,
                      isRowSelected: (item) => reOpenEntities?.otherAssignment?.id == item.id,
                      isRowSelectable: (item) => !!item,
                    }
                  : undefined
              }
              items={openEntities.otherAssignment ? [openEntities.otherAssignment] : []}
              onRowClick={(item) => setAssignmentToEdit(item)}
            ></Table>
          </div>
        )}
        {/* {openEntities.carTasks && openEntities.carTasks.length > 0 && (
          <div>
            <h4>{i18n.t.carTasks.plural}</h4>
            <Table<CarTask>
              head={
                <>
                  {!!currentCarSubsidiary && <th></th>}
                  <th align="left">{i18n.t.common.name}</th>
                  <th align="left">{i18n.t.common.state}</th>
                  <th></th>
                </>
              }
              renderRow={(item) => (
                <>
                  <td>{item.taskName}</td>
                  <td>
                    <State
                      text={item.state ? getTaskStateText(i18n.t, item.state) : getTaskStateText(i18n.t, undefined)}
                      color={getTaskStateColor(item.state)}
                    />
                  </td>
                </>
              )}
              selectable={
                !!currentCarSubsidiary
                  ? {
                      onRowSelect: onToggleRowCarTask,
                      isRowSelected: (item) => reOpenEntities?.carTasks.includes(item),
                      isRowSelectable: (item) => !!item,
                    }
                  : undefined
              }
              items={openEntities.carTasks}
              rowActions={rowActionsTasks}
            ></Table>
          </div>
        )} */}
        {/* {openEntities.dueDates && openEntities.dueDates.length > 0 && (
          <div>
            <h4>{i18n.t.dueDates.plural}</h4>
            <Table<DueDate>
              head={
                <>
                  {!!currentCarSubsidiary && <th></th>}
                  <th align="left">{i18n.t.common.name}</th>
                  <th align="left">{i18n.t.common.date}</th>
                </>
              }
              renderRow={(item) => (
                <>
                  <td>{item.type.name}</td>
                  <td>{formatDateNumeric(item.date, i18n.language)}</td>
                </>
              )}
              selectable={
                !!currentCarSubsidiary
                  ? {
                      onRowSelect: onToggleRowDueDate,
                      isRowSelected: (item) => reOpenEntities?.dueDates.includes(item),
                      isRowSelectable: (item) => !!item,
                    }
                  : undefined
              }
              items={openEntities.dueDates}
              onRowClick={(item) => setDueDateToEdit(item)}
            ></Table>
          </div>
        )}  */}

        {openEntities.highwayPass && (
          <div>
            <h4>{i18n.t.highwayPasses.plural}</h4>
            <Table<HighwayPass>
              head={
                <>
                  {!!currentCarSubsidiary && <th></th>}
                  <th align="left">{i18n.t.common.number}</th>
                  <th align="left">{i18n.t.common.startDate}</th>
                  <th align="left">{i18n.t.common.endDate}</th>
                </>
              }
              renderRow={(item) => (
                <>
                  <td>{item.nr}</td>
                  <td>{formatDateNumeric(item.startDate, i18n.language)}</td>
                  <td>{!!item.endDate && formatDateNumeric(item.endDate, i18n.language)}</td>
                </>
              )}
              selectable={
                !!currentCarSubsidiary
                  ? {
                      onRowSelect: onToggleRowHighwayPass,
                      isRowSelected: (item) => item.id == reOpenEntities?.highwayPass?.id,
                      isRowSelectable: (item) => !!item,
                    }
                  : undefined
              }
              items={openEntities.highwayPass ? [openEntities.highwayPass] : []}
              onRowClick={(item) => setHighwayPassToEdit(item)}
            ></Table>
          </div>
        )}
        <div className={styles.warningContainer}>
          {!!carSubsidiary && (
            <WarningBox
              className={styles.warningBox}
              text={i18n.t.common.warningCloseEntities(
                formatDateNumeric(carSubsidiary.startDate, i18n.language),
                formatDateNumeric(formData.endDate!, i18n.language),
              )}
            />
          )}
          {!!currentCarSubsidiary && (
            <WarningBox
              className={styles.warningBox}
              text={i18n.t.common.warningReOpenEntities(
                formatDateNumeric(currentCarSubsidiary.startDate, i18n.language),
                formatDateNumeric(formData.prevEndDate!, i18n.language),
              )}
            />
          )}
        </div>
        <SubmitBar>
          <Button
            type="secondary"
            buttonProps={{
              onClick: () => onClose(),
            }}
          >
            {i18n.t.common.cancel}
          </Button>
          {!!carSubsidiary && (
            <Button
              buttonProps={{
                onClick: onSubmit,
              }}
            >
              {i18n.t.common.continue}
            </Button>
          )}
          {!!currentCarSubsidiary && (
            <Button
              buttonProps={{
                onClick: onSubmitAssign,
              }}
            >
              {i18n.t.common.continue}
            </Button>
          )}
        </SubmitBar>
      </ModalDialog>

      {plateToEdit && (
        <EditCarPlateDialog
          onClose={(success) => {
            success && setPlateToEdit(undefined) && onUpdate();
          }}
          carPlateId={plateToEdit.id}
        />
      )}
      {carDriverToEdit && (
        <EditCarDriverDialog
          onClose={(success) => {
            success && setCarDriverToEdit(undefined) && onUpdate();
          }}
          carDriverId={carDriverToEdit.id}
        />
      )}
      {assignmentToEdit && (
        <EditOtherCarAssignmentDialog
          onClose={(success) => {
            success && setAssignmentToEdit(undefined) && onUpdate();
          }}
          otherCarAssignmentId={assignmentToEdit.id}
        />
      )}
      {dueDateToEdit && (
        <EditDueDate
          onClose={(success) => {
            success && setDueDateToEdit(undefined) && onUpdate();
          }}
          dueDateId={dueDateToEdit.id}
        />
      )}
      {highwayPassToEdit && (
        <EditHighwayPassDialog
          onClose={(success) => {
            success && setHighwayPassToEdit(undefined) && onUpdate();
          }}
          highwayPassId={highwayPassToEdit.id}
        />
      )}

      {!!carTaskToDelete && (
        <DeleteCarTaskDialog
          carTaskId={carTaskToDelete.id}
          carId={carTaskToDelete.car.id}
          onClose={(success) => {
            success && setCarTaskToDelete(undefined) && onUpdate();
          }}
        />
      )}
    </>
  );
};

export default OpenEntitiesDialog;
